import {
    Badge,
    BadgeProps,
    Icon,
    ListItemAvatar,
    ListItemButton,
    ListItemText,
    Popover,
    PopoverActions,
    Skeleton,
    Tooltip,
    Typography,
} from '@mui/material';
import { useCallback, useRef, useState } from 'react';
import UnfoldMoreOutlinedIcon from '@mui/icons-material/UnfoldMoreOutlined';
import WorkspaceSwitcher from '../WorkspaceSwitcher';
import { useNavigation } from '@/providers/navigation-provider';
import { styled } from '@mui/material/styles';
import WorkspaceAvatar from '../workspace/WorkspaceAvatar';
import { useSession } from '@/providers/session-provider';

const StyledBadge = styled(Badge)<BadgeProps>(() => ({
    '& .MuiBadge-badge': {
        width: '0.625rem',
        height: '0.625rem',
        border: '0.125rem solid white',
        borderRadius: '0.3125rem',
    },
}));

export default function MainNavAccount() {
    const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
    const { mainNavOpen } = useNavigation();
    const popoverActions = useRef<PopoverActions>(null);
    const { workspace, loading } = useSession();

    const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    // Stable identity: WorkspaceSwitcher repositions the popover whenever this changes.
    const handlePopoverLoadingComplete = useCallback(() => popoverActions.current?.updatePosition(), []);

    const open = Boolean(anchorEl);

    return (
        <>
            <Tooltip title={workspace.name} arrow placement="right" disableHoverListener={mainNavOpen}>
                <ListItemButton
                    component={'div'} // Needed for the tooltip to work
                    sx={{ borderRadius: '99px' }}
                    {...(!mainNavOpen && { sx: { justifyContent: 'center' } })}
                    onClick={handleClick}
                    data-id="menu-account"
                >
                    <ListItemAvatar {...(!mainNavOpen && { sx: { minWidth: 0 } })}>
                        <StyledBadge
                            color="error"
                            variant="dot"
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                            badgeContent={0}
                        >
                            {!loading && workspace ? (
                                <WorkspaceAvatar
                                    name={workspace.name}
                                    color={workspace.color}
                                    picture={workspace.picture}
                                    sx={(theme) => ({
                                        width: theme.spacing(3),
                                        height: theme.spacing(3),
                                        fontSize: theme.spacing(1.5),
                                    })}
                                />
                            ) : (
                                <Skeleton
                                    variant="rounded"
                                    sx={{ width: '1.5rem', height: '1.5rem' }}
                                    animation="wave"
                                />
                            )}
                        </StyledBadge>
                    </ListItemAvatar>
                    {mainNavOpen && (
                        <>
                            <ListItemText>
                                {!loading && workspace ? (
                                    <Typography noWrap variant="body2">
                                        {workspace.name}
                                    </Typography>
                                ) : (
                                    <Skeleton variant="text" sx={{ fontSize: '1rem' }} animation="wave" />
                                )}
                            </ListItemText>
                            <Icon color="action" sx={{ display: 'flex' }}>
                                <UnfoldMoreOutlinedIcon />
                            </Icon>
                        </>
                    )}
                </ListItemButton>
            </Tooltip>
            <Popover
                action={popoverActions}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                slotProps={{ paper: { sx: { width: '100%', maxWidth: '22.5rem' } } }}
            >
                <WorkspaceSwitcher
                    popOverLoadingComplete={handlePopoverLoadingComplete}
                    onSwitch={handleClose}
                />
            </Popover>
        </>
    );
}
